import { completeName, formatDate, i18n, uppercaseInitials } from 'utils';
import { eventStateLabel } from 'utils/eventStateLabel';

export const normalizeDataUser = (payload: any) => {
  const data = payload?.data?.item;
  return data ? {
    ...data,
    completeName: completeName(data.firstName, data.middleName, data.lastName),
    initialsName: uppercaseInitials(data.firstName, data.middleName, data.lastName),
  } : null;
};

export const normalizeDataRelatives = (payload: any) => {
  const data = payload?.data?.item?.items;
  return data ? data.map((item: any) => ({
    ...item,
    ...item.userName,
    id: item.userId,
    access: i18n(item.access),
    relation: item.relationship?.code ?? '',
  })) : [];
};

export const normalizeDataMeasures = (
  payload: any,
  meta: any,
  measureTypeLookup: Record<string, string> = {},
) => {
  const data = payload?.data?.item?.items;
  if (data) {
    const dates = data.map((item: any) => formatDate(item.timestamp));
    const organizeDates = dates.reduce((prev: any, next: any) => {
      return {
        ...prev,
        [next]: data.filter((item: any) => formatDate(item.timestamp) === next),
      };
    }, 0);
    const newData = dates.reduce((prev: any, next: any) => {
      return {
        ...prev,
        [next]: organizeDates[next].map((item: any) => ({
          ...item,
          items: item.items.map((element: any) => ({
            ...element,
            measureStatus: meta.measureStatus?.find((status: any) => status.id === element.statusId),
            measureType: element.type?.code ? (measureTypeLookup[element.type.code] ?? element.type.code) : '',
          })).sort((prev: any, next: any) => {
            return prev.type.genericTypeId - next.type.genericTypeId;
          }),
        })),
      };
    }, 0);
    return newData;
  }
  return [];
};

// Grouped listing: the payload items are entries, each either a collapsed band
// (kind GROUP, with members) or a single toma (kind MEASURE). We group entries
// by date like the flat listing, decorating the underlying measures the same way
// so the cards render identically; group entries are tagged with __group and
// carry their decorated members for in-place expansion.
export const normalizeDataMeasuresGrouped = (
  payload: any,
  meta: any,
  measureTypeLookup: Record<string, string> = {},
) => {
  const entries = payload?.data?.item?.items;
  if (!entries) return [];

  const decorateMeasure = (measure: any) => ({
    ...measure,
    items: (measure.items ?? []).map((element: any) => ({
      ...element,
      measureStatus: meta.measureStatus?.find((status: any) => status.id === element.statusId),
      measureType: element.type?.code ? (measureTypeLookup[element.type.code] ?? element.type.code) : '',
    })).sort((prev: any, next: any) => prev.type.genericTypeId - next.type.genericTypeId),
  });

  // Group bands are attributed to the wake day (the instant just before band end),
  // so a sleep band that crosses midnight shows under the day you wake up.
  const entryTimestamp = (entry: any) =>
    entry.kind === 'GROUP' ? entry.group?.bandEndTs - 1 : entry.measure?.timestamp;

  const entryNode = (entry: any) =>
    entry.kind === 'GROUP'
      ? { __group: true, ...entry.group, members: (entry.group?.members ?? []).map(decorateMeasure) }
      : decorateMeasure(entry.measure);

  const byDate: Record<string, any[]> = {};
  entries.forEach((entry: any) => {
    const date = formatDate(entryTimestamp(entry));
    (byDate[date] = byDate[date] ?? []).push(entryNode(entry));
  });

  return Object.keys(byDate).length ? byDate : 0;
};

export const normalizeDataMeasuresDate = (
  payload: any,
  meta: any,
  measureTypeLookup: Record<string, string> = {},
  measureGenericTypeLookup: Record<string, string> = {},
) => {
  const data = payload?.data?.items;
  if (data) {
    const dates = data.map((item: any) => formatDate(item.timestamp));
    const organizeDates = dates.reduce((prev: any, next: any) => {
      return {
        ...prev,
        [next]: data.filter((item: any) => formatDate(item.timestamp) === next),
      };
    }, 0);
    const newData = dates.reduce((prev: any, next: any) => {
      return {
        ...prev,
        [next]: organizeDates[next].map((item: any) => ({
          ...item,
          measureNotes: item.measureNotes ? item.measureNotes.sort((prev: any, next: any) => {
            return next.timestamp - prev.timestamp;
          }) : [],
          items: item.items.map((element: any) => {
            const genericType = meta.measuresGenericTypes?.find((g: any) => g.id === element.type.genericTypeId);
            const genericCode = genericType?.code;
            return {
              ...element,
              measureStatus: meta.measureStatus?.find((status: any) => status.id === element.statusId),
              measureType: element.type?.code ? (measureTypeLookup[element.type.code] ?? element.type.code) : '',
              measureGenericTypeCode: genericCode ? (measureGenericTypeLookup[genericCode] ?? genericCode) : '',
            };
          }).sort((prev: any, next: any) => {
            return prev.type.genericTypeId - next.type.genericTypeId;
          }),
        })),
      };
    }, 0);
    return newData;
  }
  return [];
};

export const updateNotes = (payload: any) => payload.notes.filter((item: any) => item.id !== payload.id);

export const updateMeasureNotes = (payload: any, measureData: any) => {
  // Actualización optimista solo posible si el payload trae la fecha/medida a la
  // que pertenece la nota. Con el payload de postPatientMeasureNoteAsync
  // ({ newNote: { referenceId, note }, applicantName }) no viene, así que
  // devolvemos los datos sin tocar (el modal refresca vía getMeasureAsync) en
  // lugar de reventar con measureData[undefined].map.
  const list = measureData?.[payload?.measureDate];
  if (!Array.isArray(list)) {
    return measureData;
  }
  return {
    ...measureData,
    [payload.measureDate]: list.map((item: any) => {
      if (item.id === payload.newNote.measureId) {
        return {
          ...item,
          measureNotes: [...item.measureNotes, {
            measureId: payload.newNote.measureId,
            value: payload.newNote.value,
            applicantName: payload.applicantName,
            timestamp: payload.newNote.timestamp,
          }],
        };
      }
      return item;
    }),
  };
};

export const updateHealthData = (payload: any, data: any) => {
  let newData = {};
  if (payload.type === 'pathologies' || payload.type === 'observations') {
    newData = {
      ...data,
      [payload.type]: payload.dataValue,
    };
  } else {
    newData = {
      ...data,
      [payload.type]: [...data[payload.type], { id: 1, value: payload.dataValue }],
    };
  }
  return newData;
};

export const delHealthData = (payload: any, data: any) => {
  let newData = {};
  if (payload.type === 'pathologies') {
    newData = {
      ...data,
      [payload.type]: data[payload.type].filter((item: any) => item !== payload.valueId),
    };
  } else {
    newData = {
      ...data,
      [payload.type]: data[payload.type].filter((item: any) => item.id !== payload.valueId),
    };
  }
  return newData;
};

export const normalizeHistoricServiceState = (payload: any, serviceStateLookup: Record<string, string> = {}) => {
  const data = payload?.data?.item?.items;
  return data ? data.map((item: any) => ({
    ...item,
    serviceStateCode: item.serviceState.code ? (serviceStateLookup[item.serviceState.code] ?? item.serviceState.code) : '',
    serviceStateReasonCode: item?.serviceStateReason?.code != null ? (item.serviceStateReason.label ?? item.serviceStateReason.code) : '',
  })) : [];
};

export const normalizeDataUserAction = (payload: any, meta: any) => {
  const data = payload?.data?.item?.items;
  return data ? data.map((item: any) => ({
    ...item,
    patientId: item.patientId,
    date: item.eventTimestamp,
    user: `${item.patientName.firstName !== null ? item.patientName.firstName : ''} ${item.patientName.middleName !== null ? item.patientName.middleName : ''} ${item.patientName.lastName !== null ? item.patientName.lastName : ''}`,
    firstName: item.patientName.firstName !== null ? item.patientName.firstName : '',
    middleName: item.patientName.middleName !== null ? item.patientName.middleName : '',
    lastName: item.patientName.lastName !== null ? item.patientName.lastName : '',
    actionType: i18n(item.eventType),
    alarmState: eventStateLabel(item),
    rawActionType: item.eventType,
    patientServiceNumber: item.patientServiceNumber,
    destinationNumber: item.destinationNumber,
    deviceActionId: item.eventId,
    deviceId: item.deviceId,
    deviceTypeCode: item.deviceTypeCode,
    devicePeripheralId: item.devicePeripheralId,
    devicePeripheralTypeCode: item.devicePeripheralTypeCode,
    patientGroup: item.patientGroup,
    group: item.patientGroup.code,
    lastDeviceLocation: item.lastDeviceLocation,
    hasNotes: item.hasNotes,
    resolved: !!item.resolution.resolution.id,
    resolutionId: item.resolution.resolution.id,
    resolutionTimestamp: item.resolution.timestamp,
    measureRawId: item.measureRawId,
    reference: item.reference,
    applicantName: item.resolution.applicantName,
  })) : [];
};

export const updateRelatives = (data: any, meta: any) => {
  const newData = data.map((item: any) => {
    if (item.relationId === meta.relationId) {
      return {
        ...item,
        relationship: { ...meta.relationship },
      };
    }
    return item;
  });
  return newData;
};

export const normalizeNotesAllTypes = (payload: any, resolutionLookup: Record<string, string> = {}) => {
  const data = payload?.data?.item?.items;
  return data ? data.map((item: any) => ({
    ...item,
    resolution: item.resolution !== null ? (resolutionLookup[item.resolution] ?? item.resolution) : '',
    noteFromType: i18n(item.noteFrom),
  })) : [];
};

export const normalizePatientAgenda = (payload: any) => {
  const data = payload?.data;
  return data ? data.map((item: any) => ({
    ...item,
    observation: item.observation === null ? '' : item.observation,
    activityTypeName: i18n(item.activityType.name),
    frequencyAux: i18n('frequency.' + item.frequency)
  })) : [];
};

export const normalizePatientAgendaEvents = (payload: any) => {
  const data = payload?.data?.data;
  return data ? data.map((item: any, index: number) => ({
    ...item,
    number: index + 1 + 'º',
    notifiedBy: item.notifiedBy !== null ? i18n('agendas.notifiedBy.' + item.notifiedBy) : ''
  })) : [];
};
